import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BIVizDashboardSales } from '../sql-maps/biviz-dashboard-sales-datasource-sql-map';

interface IABTestFLaggedUsersMode {
    /**
     * If true, the DataLoader will use ABTest flagged users mode.
     * If false, the DataLoader will use regular mode.
     * @default false
     */
    abTestFlaggedUsersMode?: boolean;
}

export interface RevenueARPUDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions,
        IABTestFLaggedUsersMode {
    /**
     * Pass callback to retrieve max day no for the query.
     * If not provided, the default max day no will be used.
     * @param context - query context
     * @returns Must return a valid SQL string
     */
    maxDayNoGetter?: (context: IQueryContext) => string;
}

export class RevenueARPUDataLoaderFactory extends Factory<
    DataLoader,
    RevenueARPUDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsARPUDataLoaderFactory';

    /** This function returns DataLoader instance for analytics vs store reports differences */
    getInstance(options: RevenueARPUDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log('RevenueARPUDataLoaderFactory', queryContext);

                // ignore columns that are not supported in this query
                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ];

                const revenueAgg = BIVizDashboardSales;

                // identify columns not supported in datasource
                const revenueAggDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        revenueAgg.sqlMap.columns
                    );

                const revenueAggDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        revenueAggDsColumns.missing
                    );

                //filter out ignored columns from dsColumns.present
                revenueAggDsColumns.present =
                    revenueAggDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const revenueAggSQL =
                    revenueAgg.sqlStatementsCompiler.compileSQLStatements(
                        revenueAggDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: ['day_no = 0'],
                // };

                const usersdashActivity = BiVizDashboardUsersActivity;

                // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );

                const usersdashActivityDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        usersdashActivityDsColumns.missing
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usd_pln =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';

                // TODO refactor datasource column names

                // if only cohort split group then 'cohort_split_group AS split_group'
                // if only ads flow split group then 'ads_flow_split_group AS split_group'
                // if both then 'CONCAT(cohort_split_group, ', ', ads_flow_split_group) AS split_group'
                // if none then ''

                const windowRunning = `${
                    revenueAggSQL.splitGroupBy != ''
                        ? 'PARTITION BY c.split_group '
                        : ''
                }ORDER BY c.day_no`;

                const windowTotal = `${
                    revenueAggSQL.splitGroupBy != ''
                        ? 'PARTITION BY c.split_group '
                        : ''
                }`;

                const getDefaultMaxDayNo = (context: IQueryContext) => {
                    return `MAX(day_no) OVER (PARTITION BY ${usersdashActivity.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL})`;
                };

                const maxDayNo = options.maxDayNoGetter
                    ? options.maxDayNoGetter(queryContext) !== 'unlimited'
                        ? `LEAST(${options.maxDayNoGetter(
                              queryContext
                          )}, ${getDefaultMaxDayNo(queryContext)})`
                        : getDefaultMaxDayNo(queryContext)
                    : getDefaultMaxDayNo(queryContext);

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH revenue AS
(
  SELECT
    ${revenueAggSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    *
  FROM 
  (
    SELECT
      ${revenueAggSQL.cohortSplitSelect}
      ${revenueAggSQL.datasourceSpecificSplitSelect}

      day_no,
      SUM(revenue_net_usd) AS revenue_net_usd,
      SUM(revenue_net_pln) AS revenue_net_pln,
      SUM(revenue_gross_usd) AS revenue_gross_usd,
      SUM(revenue_gross_pln) AS revenue_gross_pln,
      SUM(inapp_revenue_net_usd) AS inapp_revenue_net_usd,
      SUM(inapp_revenue_net_pln) AS inapp_revenue_net_pln,
      SUM(inapp_revenue_gross_usd) AS inapp_revenue_gross_usd,
      SUM(inapp_revenue_gross_pln) AS inapp_revenue_gross_pln,
      SUM(ad_revenue_net_usd) AS ad_revenue_net_usd,
      SUM(ad_revenue_net_pln) AS ad_revenue_net_pln,
      SUM(ad_revenue_gross_usd) AS ad_revenue_gross_usd,
      SUM(ad_revenue_gross_pln) AS ad_revenue_gross_pln,
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        revenueAgg.sqlMap.dataset,
        revenueAgg.sqlMap.tableId
    )}\` ds
    ${revenueAggSQL.join}
    ${revenueAggSQL.where}
    GROUP BY ${revenueAggSQL.cohortSplitGroupBy}${
        revenueAggSQL.datasourceSpecificSplitGroupBy
    }day_no
  )
)
${
    // cohort base korzysta z tabeli usersdash_activity i nie ma informacji o ads_flow_split_group
    // dlatego jeśli jest w context to musimy zrobić cross join
    // jeśli nie ma w context to nie ma sensu robić cross join
    // po cross join korzystamy z adsFlowDailySQL
    ''
}
, cohort_base AS
(
    SELECT
      ${
          usersdashActivitySQL.splitGroupBy != ''
              ? `${usersdashActivitySQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      ds.*
      , ${maxDayNo} AS max_day_no
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivitySQL.join}
    ${usersdashActivitySQL.where}
)

, cohort_size AS
(
  SELECT
    ${revenueAggSQL.splitGroupBy}
    day_no,
    SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${revenueAggSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
      ${BIVizDashboardSales.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL},
      COUNT(DISTINCT player_id) AS cohort_size,
      GENERATE_ARRAY(0, max_day_no ) AS days
    FROM
    (
      SELECT cohort_base.*
      FROM cohort_base
      WHERE day_no = 0
    )
    ${
        revenueAggSQL.is_in_context_split_datasource_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT revenue_split_group FROM revenue)`
            : ''
    }
    GROUP BY ${revenueAggSQL.splitGroupBy}${
        BIVizDashboardSales.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    }, max_day_no
  ), UNNEST(days) AS day_no 
  GROUP BY ${revenueAggSQL.splitGroupBy}day_no
)

, by_fod AS
(
SELECT
  ${revenueAggSQL.splitGroupBy != '' ? 'c.split_group,' : ''}
  c.day_no,

  
  ROUND(SAFE_DIVIDE(revenue_net_${usd_pln}, cohort_size), 3) AS ARPU_daily_net_${usd_pln},
  ROUND(SAFE_DIVIDE(revenue_gross_${usd_pln}, cohort_size), 3) AS ARPU_daily_gross_${usd_pln},
  ROUND(SAFE_DIVIDE(inapp_revenue_net_${usd_pln}, cohort_size), 3) AS inapp_ARPU_daily_net_${usd_pln},
  ROUND(SAFE_DIVIDE(inapp_revenue_gross_${usd_pln}, cohort_size), 3) AS inapp_ARPU_daily_gross_${usd_pln},
  ROUND(SAFE_DIVIDE(ad_revenue_net_${usd_pln}, cohort_size), 3) AS ad_ARPU_daily_net_${usd_pln},
  ROUND(SAFE_DIVIDE(ad_revenue_gross_${usd_pln}, cohort_size), 3) AS ad_ARPU_daily_gross_${usd_pln},
    
  ROUND(SUM(SAFE_DIVIDE(revenue_net_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS ARPU_net_${usd_pln},
  ROUND(SUM(SAFE_DIVIDE(revenue_gross_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS ARPU_gross_${usd_pln},
  ROUND(SUM(SAFE_DIVIDE(inapp_revenue_net_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS inapp_ARPU_net_${usd_pln},
  ROUND(SUM(SAFE_DIVIDE(inapp_revenue_gross_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS inapp_ARPU_gross_${usd_pln},
  ROUND(SUM(SAFE_DIVIDE(ad_revenue_net_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS ad_ARPU_net_${usd_pln},
  ROUND(SUM(SAFE_DIVIDE(ad_revenue_gross_${usd_pln}, cohort_size)) OVER (${windowRunning}), 3) AS ad_ARPU_gross_${usd_pln},

  ROUND(SUM(revenue_net_${usd_pln}) OVER (${windowRunning}), 2) AS running_revenue_net_${usd_pln},
  ROUND(SUM(revenue_gross_${usd_pln}) OVER (${windowRunning}), 2) AS running_revenue_gross_${usd_pln},
  ROUND(SUM(inapp_revenue_net_${usd_pln}) OVER (${windowRunning}), 2) AS inapp_running_revenue_net_${usd_pln},
  ROUND(SUM(inapp_revenue_gross_${usd_pln}) OVER (${windowRunning}), 2) AS inapp_running_revenue_gross_${usd_pln},
  ROUND(SUM(ad_revenue_net_${usd_pln}) OVER (${windowRunning}), 2) AS ad_running_revenue_net_${usd_pln},
  ROUND(SUM(ad_revenue_gross_${usd_pln}) OVER (${windowRunning}), 2) AS ad_running_revenue_gross_${usd_pln},

  ROUND(SAFE_DIVIDE(SUM(revenue_net_${usd_pln}) OVER (${windowRunning}), SUM(revenue_net_${usd_pln}) OVER (${windowTotal})), 4) AS running_p_total_revenue_net_${usd_pln},
  ROUND(SAFE_DIVIDE(SUM(revenue_gross_${usd_pln}) OVER (${windowRunning}), SUM(revenue_gross_${usd_pln}) OVER (${windowTotal})), 4) AS running_p_total_revenue_gross_${usd_pln},
  ROUND(SAFE_DIVIDE(SUM(inapp_revenue_net_${usd_pln}) OVER (${windowRunning}), SUM(inapp_revenue_net_${usd_pln}) OVER (${windowTotal})), 4) AS inapp_running_p_total_revenue_net_${usd_pln},
  ROUND(SAFE_DIVIDE(SUM(inapp_revenue_gross_${usd_pln}) OVER (${windowRunning}), SUM(inapp_revenue_gross_${usd_pln}) OVER (${windowTotal})), 4) AS inapp_running_p_total_revenue_gross_${usd_pln},
  ROUND(SAFE_DIVIDE(SUM(ad_revenue_net_${usd_pln}) OVER (${windowRunning}), SUM(ad_revenue_net_${usd_pln}) OVER (${windowTotal})), 4) AS ad_running_p_total_revenue_net_${usd_pln},
  ROUND(SAFE_DIVIDE(SUM(ad_revenue_gross_${usd_pln}) OVER (${windowRunning}), SUM(ad_revenue_gross_${usd_pln}) OVER (${windowTotal})), 4) AS ad_running_p_total_revenue_gross_${usd_pln},

  FROM cohort_size c
  LEFT JOIN revenue b 
  ON c.day_no = b.day_no
  ${revenueAggSQL.splitGroupBy != '' ? `AND c.split_group = b.split_group` : ''}
)
  
SELECT * FROM by_fod
ORDER BY ${revenueAggSQL.splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data,
                    queryContext,
                    status: revenueAggDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        revenueAggDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

export class RevenueARPUDataLoaderDocumentation {
    static dataLoaderSourcesDocumentation = /* HTML */ `<h2>Sources</h2>
        <pre>
${generateTableFullPath(
                currentDataProject().gcpProjectId,
                BIVizDashboardSales.sqlMap.dataset,
                BIVizDashboardSales.sqlMap.tableId
            )}</pre
        >
        <pre>
${generateTableFullPath(
                currentDataProject().gcpProjectId,
                BiVizDashboardUsersActivity.sqlMap.dataset,
                BiVizDashboardUsersActivity.sqlMap.tableId
            )}</pre
        >`;
    // metrics documentation
    static metrics = {
        ARPU_daily: {
            chartDocumentation: /* HTML */ `<h2>Definition</h2>
                <p>Daily coefficient of entire cohort revenue.</p>
                <h2>Calculation</h2>
                <p>
                    ARPU daily is daily revenue divided by the number of users
                    who matured to given day (cohort size for given day).
                </p>
                ${RevenueARPUDataLoaderDocumentation.dataLoaderSourcesDocumentation}`,
        },
        running_ARPU: {
            chartDocumentation: /* HTML */ `<h2>Definition</h2>
                <p>Average revenue per user (ARPU) for the day.</p>
                <h2>Calculation</h2>
                <p>Running sum of ARPU daily.</p>
                <p>
                    ARPU daily is daily revenue divided by the number of users
                    who matured to given day (cohort size for given day).
                </p>
                ${RevenueARPUDataLoaderDocumentation.dataLoaderSourcesDocumentation}`,
        },
        running_revenue: {
            chartDocumentation: /* HTML */ `<h2>Definition</h2>
                <p>Cumulative revenue of the cohort up to the given day number.</p>
                <h2>Calculation</h2>
                <p>
                    Running sum of daily cohort revenue (revenue from users
                    who matured to given day). Last point equals the total
                    revenue of the cohort within the day number limit.
                </p>
                ${RevenueARPUDataLoaderDocumentation.dataLoaderSourcesDocumentation}`,
        },
    };
}
